import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { isPublicRoute } from '../utils/themeUtils';

const THEME_MODE_STORAGE_KEY = 'coachhub_color_mode';
const AUTH_PATHS = ['/login', '/register', '/reset-password'];

const ThemeModeContext = createContext({
  themeMode: 'light',
  isDark: false,
  setThemeMode: () => {},
  toggleThemeMode: () => {},
});

/**
 * Evaluates whether dark mode should be active based on mode and system preference
 */
function getSystemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}


/**
 * Apply dark class to <html> element, strictly respecting public routes.
 * When on the landing page or public website, dark mode is NEVER applied
 * from user dashboard preferences — it stays light (controlled by Super Admin).
 */
export function applyHtmlDarkClass(isDark, pathname) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const path = pathname || window.location.pathname;
  // Login / register / password pages follow the user's theme (they have a theme switch);
  // the public website stays light.
  const publicPath = isPublicRoute(path) && !AUTH_PATHS.includes(path);

  if (isDark && !publicPath) {
    root.classList.add('dark');
    root.style.colorScheme = 'dark';
  } else {
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
  }
}

export function ThemeModeProvider({ children }) {
  const [themeMode, setThemeModeState] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    try {
      const saved = localStorage.getItem(THEME_MODE_STORAGE_KEY);
      if (saved === 'dark' || saved === 'light' || saved === 'system') {
        return saved;
      }
    } catch {}
    return 'light';
  });

  const [systemIsDark, setSystemIsDark] = useState(getSystemPrefersDark);

  // Calculate if the effective view is currently dark
  const isDark = themeMode === 'dark' || (themeMode === 'system' && systemIsDark);

  // Listen to system preference changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setSystemIsDark(e.matches);
    };

    setSystemIsDark(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Synchronize <html> class whenever isDark changes
  useEffect(() => {
    applyHtmlDarkClass(isDark);
  }, [isDark]);

  // Printed documents (payslips, attendance sheets, guides) are always light: dark text
  // colours would otherwise print white on paper. Restore the app theme afterwards.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const root = document.documentElement;
    let restoreDark = false;
    const beforePrint = () => {
      restoreDark = root.classList.contains('dark');
      if (restoreDark) applyHtmlDarkClass(false);
    };
    const afterPrint = () => {
      if (restoreDark) applyHtmlDarkClass(true);
      restoreDark = false;
    };
    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);
    return () => {
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, []);

  const setThemeMode = useCallback((mode) => {
    if (mode !== 'light' && mode !== 'dark' && mode !== 'system') return;
    setThemeModeState(mode);
    try {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    } catch {}
  }, []);

  const toggleThemeMode = useCallback(() => {
    // If currently dark (either by 'dark' or 'system'), switch to light, else switch to dark
    const nextMode = isDark ? 'light' : 'dark';
    setThemeMode(nextMode);
  }, [isDark, setThemeMode]);

  return (
    <ThemeModeContext.Provider value={{ themeMode, isDark, setThemeMode, toggleThemeMode }}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
}
